import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Scan, Loader2, Plus, Minus, Check, AlertCircle, X,
  Leaf, Factory, AlertTriangle, ShieldCheck, ChevronDown, ChevronUp
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import logger from '@/utils/logger';

const API = '/api';

const NOVA_LABELS = {
  1: { label: 'Unprocessed', icon: Leaf, color: '#5A8C6A' },
  2: { label: 'Culinary Ingredient', icon: Leaf, color: '#8CAC5A' },
  3: { label: 'Processed', icon: Factory, color: '#E5A84B' },
  4: { label: 'Ultra-Processed', icon: AlertTriangle, color: '#9A9590' },
};

export const BarcodeScanner = ({ onMealLogged, mealType }) => {
  const { authHeaders } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [product, setProduct] = useState(null);
  const [servings, setServings] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualBarcode, setManualBarcode] = useState('');
  const scannerRef = useRef(null);
  const scannerContainerId = 'barcode-scanner-region';

  const stopScanning = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.stop().then(() => {
        scannerRef.current.clear();
        scannerRef.current = null;
      }).catch(() => {
        try { scannerRef.current?.clear(); } catch {}
        scannerRef.current = null;
      });
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        try { scannerRef.current.clear(); } catch {}
      }
    };
  }, []);

  const startScanning = useCallback(async () => {
    setError('');
    setProduct(null);

    try {
      const html5Qrcode = new Html5Qrcode(scannerContainerId);
      scannerRef.current = html5Qrcode;
      setScanning(true);

      await html5Qrcode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 280, height: 150 },
          aspectRatio: 1.333,
          formatsToSupport: [
            0,  // QR_CODE
            2,  // EAN_13
            3,  // EAN_8
            11, // UPC_A
            12, // UPC_E
            4,  // CODE_128
            1,  // AZTEC (catch-all)
          ],
        },
        (decodedText) => {
          stopScanning();
          lookupBarcode(decodedText);
        },
        () => {} // ignore per-frame errors
      );
    } catch (err) {
      logger.error('Scanner start error:', err);
      setScanning(false);
      if (err?.toString().includes('NotAllowed') || err?.toString().includes('Permission')) {
        setError('Camera access denied. Please allow camera permission or enter barcode manually.');
      } else if (err?.toString().includes('NotFound') || err?.toString().includes('No camera')) {
        setError('No camera found. Please enter the barcode manually.');
      } else {
        setError('Could not start camera. Try entering the barcode manually below.');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopScanning]);

  const lookupBarcode = async (barcode) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/barcode/${barcode}`, authHeaders());
      if (res.data.found) {
        setProduct(res.data);
      } else {
        setError("Product not found. Try photographing the nutrition label instead.");
      }
    } catch (err) {
      setError('Failed to look up barcode. Please try again.');
    }
    setLoading(false);
  };

  const handleConfirm = async () => {
    if (!product) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/meals/barcode`, {
        barcode: product.barcode,
        servings,
        product,
        meal_type: mealType,
      }, authHeaders());
      onMealLogged(res.data);
    } catch (err) {
      setError('Failed to log meal. Please try again.');
    }
    setLoading(false);
  };

  const perServing = product?.per_serving || {};
  const adjustedCals = Math.round((perServing.calories || 0) * servings);
  const adjustedP = Math.round((perServing.protein || 0) * servings * 10) / 10;
  const adjustedC = Math.round((perServing.carbs || 0) * servings * 10) / 10;
  const adjustedF = Math.round((perServing.fat || 0) * servings * 10) / 10;

  const [showSpotlight, setShowSpotlight] = useState(false);
  const [alternatives, setAlternatives] = useState([]);
  const [loadingAlts, setLoadingAlts] = useState(false);

  useEffect(() => {
    if (product && product.nuri_score != null && product.nuri_score < 70) {
      setLoadingAlts(true);
      axios.get(`${API}/barcode/${product.barcode}/alternatives`, authHeaders())
        .then(res => setAlternatives(res.data?.alternatives || []))
        .catch(() => {})
        .finally(() => setLoadingAlts(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.barcode]);

  // Product result view
  if (product) {
    const nuriScore = product.nuri_score;
    const scoreColor = product.nuri_score_color === 'green' ? '#5A8C6A'
      : product.nuri_score_color === 'amber' ? '#E5A84B' : '#9A9590';
    const nova = product.nova_group;
    const novaInfo = nova ? NOVA_LABELS[nova] : null;
    const NovaIcon = novaInfo?.icon || Factory;
    const spotlight = product.ingredient_spotlight;
    const hmFlags = product.health_mode_flags || [];
    const circumference = 2 * Math.PI * 42;
    const dashOffset = nuriScore != null ? circumference * (1 - nuriScore / 100) : circumference;

    return (
      <div className="animate-slide-up-spring" data-testid="barcode-result">
        <div className="bg-[#1E2A32] rounded-2xl p-6 border border-white/10">
          {/* Product header */}
          <div className="mb-4">
            <h3 className="text-xl font-bold text-[#F0EDE8]">{product.product_name}</h3>
            {product.brand && <p className="text-sm text-[#A1A1AA] mt-1">{product.brand}</p>}
            <p className="text-xs text-[#A1A1AA] mt-1">Serving: {product.serving_size}</p>
          </div>

          {/* Nuri Score Ring + NOVA side-by-side */}
          {nuriScore != null && (
            <div className="flex items-center justify-center gap-6 mb-5" data-testid="nuri-score-section">
              <div className="relative" style={{ width: 100, height: 100 }}>
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#2a3540" strokeWidth="6" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={scoreColor} strokeWidth="6"
                    strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dashOffset}
                    transform="rotate(-90 50 50)"
                    style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold" style={{ color: scoreColor }}>{nuriScore}</span>
                  <span className="text-[9px] font-medium text-[#A1A1AA] uppercase tracking-wider">Nuri Score</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                {product.nuri_score_explanation && (
                  <p className="text-xs text-[#A1A1AA] leading-relaxed mb-2">{product.nuri_score_explanation}</p>
                )}
                {novaInfo && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-[#141C22]" data-testid="nova-indicator">
                    <NovaIcon className="w-4 h-4 flex-shrink-0" style={{ color: novaInfo.color }} />
                    <div>
                      <p className="text-xs font-medium" style={{ color: novaInfo.color }}>NOVA {nova}</p>
                      <p className="text-[10px] text-[#A1A1AA]">{novaInfo.label}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Health mode flags — explicit profile violation messages */}
          {hmFlags.length > 0 && (
            <div className="mb-4 p-3 rounded-xl bg-[#C67A5C]/8 border border-[#C67A5C]/20" data-testid="health-mode-flags">
              {hmFlags.map((f, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{
                    color: f.severity === 'critical' ? '#D4736A' : f.severity === 'warning' ? '#E5A84B' : '#9A9590'
                  }} />
                  <span className="text-xs leading-relaxed" style={{
                    color: f.severity === 'critical' ? '#D4736A' : f.severity === 'warning' ? '#E5A84B' : '#A1A1AA'
                  }}>{f.message || f.ingredient}</span>
                </div>
              ))}
            </div>
          )}

          {/* Macros */}
          <div className="text-center mb-4">
            <div className="text-4xl font-bold text-[#F0EDE8]">{adjustedCals}</div>
            <div className="text-sm text-[#A1A1AA]">calories</div>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-5">
            {[
              { label: 'Protein', val: adjustedP, color: '#4A90D9' },
              { label: 'Carbs', val: adjustedC, color: '#E5A84B' },
              { label: 'Fat', val: adjustedF, color: '#D4736A' },
            ].map(m => (
              <div key={m.label} className="text-center">
                <div className="text-2xl font-bold" style={{ color: m.color }}>{m.val}g</div>
                <div className="text-xs text-[#A1A1AA]">{m.label}</div>
              </div>
            ))}
          </div>

          {/* Ingredient Spotlight */}
          {spotlight?.has_data && (
            <div className="mb-4" data-testid="ingredient-spotlight">
              <button onClick={() => setShowSpotlight(!showSpotlight)}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-[#141C22] hover:bg-[#1a2530] transition">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#6B9E8A]" />
                  <span className="text-xs font-medium text-[#F0EDE8]">Ingredient Spotlight</span>
                  <span className="text-[10px] text-[#A1A1AA]">
                    {(spotlight.good?.length || 0) + (spotlight.concerning?.length || 0)} items
                  </span>
                </div>
                {showSpotlight ? <ChevronUp className="w-4 h-4 text-[#A1A1AA]" /> : <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />}
              </button>
              {showSpotlight && (
                <div className="mt-2 p-3 rounded-xl bg-[#141C22] space-y-1">
                  {spotlight.health_mode_flags?.map((f, i) => (
                    <div key={`hm-${i}`} className="flex items-start gap-2 py-1.5 border-b border-white/5">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{
                        color: f.severity === 'critical' ? '#D4736A' : '#E5A84B'
                      }} />
                      <div>
                        <span className="text-xs font-medium" style={{
                          color: f.severity === 'critical' ? '#D4736A' : '#E5A84B'
                        }}>{f.ingredient}</span>
                        <p className="text-[10px] text-[#A1A1AA]">{f.message || `Flagged for your ${(f.mode || '').replace(/_/g, ' ')} profile`}</p>
                      </div>
                    </div>
                  ))}
                  {spotlight.concerning?.map((c, i) => (
                    <div key={`c-${i}`} className="flex items-start gap-2 py-1.5 border-b border-white/5">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#E5A84B]" />
                      <div>
                        <span className="text-xs font-medium text-[#E5A84B]">{c.ingredient}</span>
                        <p className="text-[10px] text-[#A1A1AA]">{c.reason}</p>
                      </div>
                    </div>
                  ))}
                  {spotlight.good?.map((g, i) => (
                    <div key={`g-${i}`} className="flex items-center gap-2 py-1.5">
                      <Check className="w-3.5 h-3.5 flex-shrink-0 text-[#5A8C6A]" />
                      <span className="text-xs text-[#A1A1AA]">{g.ingredient}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Healthier Alternatives */}
          {(alternatives.length > 0 || loadingAlts) && (
            <div className="mb-4" data-testid="healthier-alternatives">
              <p className="text-xs font-medium text-[#A1A1AA] uppercase tracking-wider mb-2">Healthier Alternatives</p>
              {loadingAlts ? (
                <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 text-[#6B9E8A] animate-spin" /></div>
              ) : alternatives.map((alt, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#141C22] mb-2"
                  data-testid={`alt-product-${i}`}>
                  <div className="flex-shrink-0 text-center">
                    <span className="text-lg font-bold" style={{ color: alt.nuri_score_color === 'green' ? '#5A8C6A' : '#E5A84B' }}>
                      {alt.nuri_score}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#F0EDE8] truncate">{alt.product_name}</p>
                    <p className="text-[10px] text-[#A1A1AA]">{alt.brand}</p>
                    {alt.reason && <p className="text-[10px] text-[#6B9E8A] mt-0.5">{alt.reason}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Servings */}
          <div className="flex items-center justify-center gap-4 mb-3 p-4 bg-[#141C22] rounded-xl">
            <button data-testid="serving-minus" onClick={() => setServings(Math.max(0.25, servings - 0.25))}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20">
              <Minus className="w-5 h-5 text-[#F0EDE8]" />
            </button>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#F0EDE8]">{servings}</div>
              <div className="text-xs text-[#A1A1AA]">serving(s)</div>
            </div>
            <button data-testid="serving-plus" onClick={() => setServings(servings + 0.25)}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20">
              <Plus className="w-5 h-5 text-[#F0EDE8]" />
            </button>
          </div>
          <div className="flex justify-center gap-2 mb-5 flex-wrap">
            {[0.25, 0.5, 0.75, 1, 1.5, 2, 3].map(mult => (
              <button key={mult} data-testid={`serving-preset-${mult}`} onClick={() => setServings(mult)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  servings === mult ? 'bg-[#6B9E8A] text-white' : 'bg-white/10 text-[#A1A1AA] hover:bg-white/20'
                }`}>
                {mult === 0.25 ? '¼' : mult === 0.5 ? '½' : mult === 0.75 ? '¾' : `${mult}x`}
              </button>
            ))}
          </div>

          <Button data-testid="barcode-confirm-btn" onClick={handleConfirm} disabled={loading}
            className="w-full h-14 rounded-xl bg-[#6B9E8A] text-white font-semibold">
            {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Check className="w-5 h-5 mr-2" />}
            Confirm & Log
          </Button>
          <button onClick={() => { setProduct(null); setServings(1); setShowSpotlight(false); }}
            className="w-full text-center text-sm text-[#A1A1AA] font-medium py-3 hover:text-[#F0EDE8]">
            Scan Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="barcode-scanner">
      {/* Scanner area */}
      {scanning ? (
        <div className="relative rounded-2xl overflow-hidden bg-black mb-4">
          <div id={scannerContainerId} className="w-full" />
          <button
            onClick={stopScanning}
            className="absolute top-3 right-3 z-20 p-2 rounded-full bg-black/60 text-white hover:bg-black/80"
            data-testid="barcode-cancel-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <div
          onClick={startScanning}
          data-testid="barcode-start-btn"
          className="aspect-[4/3] rounded-2xl border-2 border-dashed border-white/15 flex flex-col items-center justify-center cursor-pointer hover:border-white/30 bg-[#1E2A32] transition-colors"
        >
          <div id={scannerContainerId} className="hidden" />
          <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
            <Scan className="w-8 h-8 text-[#6B9E8A]" />
          </div>
          <p className="font-medium text-[#F0EDE8]">Tap to scan barcode</p>
          <p className="text-sm text-[#A1A1AA] mt-1">Works on all phones — iPhone & Android</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-[#6B9E8A] animate-spin" />
          <span className="ml-3 text-[#A1A1AA]">Looking up product...</span>
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 rounded-xl bg-[#C67A5C]/10 flex items-start gap-3" data-testid="barcode-error">
          <AlertCircle className="w-5 h-5 text-[#C67A5C] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#C67A5C]">{error}</p>
        </div>
      )}

      {/* Manual barcode entry */}
      <div className="mt-6">
        <p className="text-sm text-[#A1A1AA] mb-2 text-center">Or enter barcode manually</p>
        <div className="flex gap-2">
          <input
            data-testid="manual-barcode-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={manualBarcode}
            onChange={e => setManualBarcode(e.target.value)}
            placeholder="Enter barcode number"
            className="flex-1 h-12 px-4 rounded-xl border border-white/10 bg-[#1E2A32] text-[#F0EDE8] placeholder:text-[#A1A1AA]/50 focus:ring-2 focus:ring-[#6B9E8A]/30 focus:border-[#6B9E8A]"
            onKeyDown={e => { if (e.key === 'Enter' && manualBarcode.trim()) lookupBarcode(manualBarcode.trim()); }}
          />
          <Button
            data-testid="manual-barcode-submit"
            onClick={() => { if (manualBarcode.trim()) lookupBarcode(manualBarcode.trim()); }}
            disabled={!manualBarcode.trim() || loading}
            className="h-12 px-6 rounded-xl bg-[#6B9E8A] text-white"
          >
            Search
          </Button>
        </div>
      </div>
    </div>
  );
};
